/**
 * TimelineTab (#180) — aba "Timeline" do workspace.
 * Feed cronológico derivado do cronograma: uma entrada por tarefa (título do
 * ticket, status, fase, responsável, data) + âncora "Projeto criado". Sem
 * backend novo — reflete o estado atual das tarefas em ordem de início.
 */

import React, { useMemo } from 'react';

import { Icon } from '@/shared/icons';

import type { ProjectGanttTask, ProjectPhase } from '../types';

type StatusCat = ProjectGanttTask['ticket'] extends infer T
  ? T extends { statusCategory: infer S }
    ? S
    : never
  : never;

/* F63: cores exclusivamente via tokens `var(--theme-*)` — nada de hex fixo
   (comportava-se certo só no tema claro). */
const STATUS: Record<string, { label: string; color: string; icon: 'circle' | 'activity' | 'clock' | 'check-circle' }> = {
  new: { label: 'Nova', color: 'var(--theme-muted-text)', icon: 'circle' },
  in_progress: { label: 'Em andamento', color: 'var(--theme-info-color)', icon: 'activity' },
  waiting: { label: 'Aguardando', color: 'var(--theme-warning-color)', icon: 'clock' },
  resolved: { label: 'Resolvida', color: 'var(--theme-success-color)', icon: 'check-circle' },
  closed: { label: 'Concluída', color: 'var(--theme-success-color)', icon: 'check-circle' },
};

function fmtDate(d?: string | Date): string {
  if (!d) return '—';
  const parsed = typeof d === 'string' ? new Date(d.length <= 10 ? `${d}T00:00:00` : d) : d;
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface TimelineItem {
  key: string;
  date: string;
  title: string;
  subtitle: string;
  color: string;
  icon: 'circle' | 'activity' | 'clock' | 'check-circle' | 'folder';
  milestone: boolean;
}

export interface TimelineTabProps {
  tasks: ProjectGanttTask[];
  phases: ProjectPhase[];
  projectName: string;
  projectCreatedAt?: string | Date;
}

export const TimelineTab: React.FC<TimelineTabProps> = ({ tasks, phases, projectName, projectCreatedAt }) => {
  const phaseById = useMemo(() => {
    const map = new Map<string, ProjectPhase>();
    phases.forEach((p) => map.set(p.id, p));
    return map;
  }, [phases]);

  const items = useMemo<TimelineItem[]>(() => {
    const taskItems: TimelineItem[] = tasks.map((t) => {
      const cat = (t.ticket?.statusCategory ?? 'new') as StatusCat as string;
      const status = STATUS[cat] ?? STATUS.new!;
      const phase = t.phaseId ? phaseById.get(t.phaseId) : undefined;
      const subParts = [status.label];
      if (phase) subParts.push(phase.name);
      if (t.ticket?.assigneeName) subParts.push(t.ticket.assigneeName);
      return {
        key: t.id,
        date: t.startDate,
        title: t.ticket?.title || 'Tarefa',
        subtitle: subParts.join(' · '),
        color: phase?.color ?? status.color,
        icon: status.icon,
        milestone: !!t.isMilestone,
      };
    });

    // Ordena por data de início (ascendente = progressão do cronograma).
    taskItems.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    const created: TimelineItem = {
      key: '__created__',
      date: typeof projectCreatedAt === 'string' ? projectCreatedAt : (projectCreatedAt?.toISOString() ?? ''),
      title: `Projeto "${projectName}" criado`,
      subtitle: 'Início do projeto',
      color: 'var(--theme-primary)',
      icon: 'folder',
      milestone: false,
    };

    return [created, ...taskItems];
  }, [tasks, phaseById, projectName, projectCreatedAt]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-6" data-testid="project-ws-timeline">
      {items.length <= 1 ? (
        <div
          className="rounded-lg border border-dashed p-10 text-center"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          <Icon name="activity" className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--theme-muted-text)' }} />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            A timeline aparece conforme as tarefas do cronograma são criadas.
          </p>
        </div>
      ) : (
        <div>
          {items.map((item, idx) => (
            <div key={item.key} className="flex gap-3 pb-5 relative" data-testid={`timeline-item-${item.key}`}>
              {/* Linha vertical conectora (exceto no último) */}
              {idx < items.length - 1 && (
                <span
                  className="absolute top-8 bottom-0 w-0.5"
                  style={{ left: 15, backgroundColor: 'var(--theme-card-border)' }}
                />
              )}
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-[1]"
                style={{ backgroundColor: item.color }}
              >
                {item.milestone ? (
                  <span style={{ width: 10, height: 10, background: '#fff', transform: 'rotate(45deg)', borderRadius: 2 }} />
                ) : (
                  <Icon name={item.icon} className="w-4 h-4" style={{ color: '#fff' }} />
                )}
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
                    {item.title}
                  </span>
                  <span className="text-xs flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }}>
                    {fmtDate(item.date)}
                  </span>
                </div>
                <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--theme-muted-text)' }}>
                  {item.subtitle}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

TimelineTab.displayName = 'TimelineTab';
